import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Eye, Edit3, CheckCircle, XCircle, Search, Filter, Calendar, X, 
  AlertTriangle, Receipt, ToggleLeft, ToggleRight, Sparkles, Plus, Award,
  UploadCloud, FileSpreadsheet, Check, CheckSquare, Square, Download, RefreshCw,
  Info, Users, DollarSign, Building, ChevronRight, FileCheck, HelpCircle, Layers,
  ArrowRight, ShieldCheck, FileText
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { buildApiUrl } from '../../utils/apiSetup';
import { formatDate } from '../../utils/dateUtils';

const FestivalAdvanceManagement = () => {
  const { hasPermission } = useAuth();
  const canView = hasPermission('festival_advance:view');
  const canAdd = hasPermission('festival_advance:add');
  const canApprove = hasPermission('festival_advance:approve');
  const [applications, setApplications] = useState([]);
  const [festivals, setFestivals] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Tab control
  const [activeTab, setActiveTab] = useState('applications');
  
  // Applications Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [festivalFilter, setFestivalFilter] = useState('all');
  const [departmentFilter, setDepartmentFilter] = useState('all');
  const [yearFilter, setYearFilter] = useState('all');

  // Modals state
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [showApplicationModal, setShowApplicationModal] = useState(false);
  const [showCreateFestivalModal, setShowCreateFestivalModal] = useState(false);
  const [showEditFestivalModal, setShowEditFestivalModal] = useState(false);
  
  // Form states
  const [festivalForm, setFestivalForm] = useState({
    festival_code: '',
    festival_name: '',
    description: '',
    default_amount: '12500.00',
    default_installments: '10'
  });
  
  const [editingFestival, setEditingFestival] = useState(null);

  // Bulk Advance State
  const [bulkFestivalId, setBulkFestivalId] = useState('');
  const [bulkFile, setBulkFile] = useState(null);
  const [bulkApprovalDate, setBulkApprovalDate] = useState(new Date().toISOString().split('T')[0]);
  const [bulkUploading, setBulkUploading] = useState(false);
  const [bulkProcessing, setBulkProcessing] = useState(false);
  const [bulkPreviewData, setBulkPreviewData] = useState(null);
  const [selectedBulkRows, setSelectedBulkRows] = useState({}); // row_number -> boolean
  const [bulkSearchTerm, setBulkSearchTerm] = useState('');
  const [bulkStatusFilter, setBulkStatusFilter] = useState('all'); // 'all', 'valid', 'errors', 'warnings'
  const [bulkSectionFilter, setBulkSectionFilter] = useState('all');
  const [showConfirmBatchModal, setShowConfirmBatchModal] = useState(false);
  const [bulkBatchResult, setBulkBatchResult] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      await Promise.all([
        fetchFestivals(),
        fetchDepartments(),
        fetchApplications()
      ]);
    } catch (err) {
      console.error('Error loading page data:', err);
      setError('Failed to load screen data');
    } finally {
      setLoading(false);
    }
  };

  const fetchApplications = async () => {
    try {
      const params = new URLSearchParams();
      params.append('approved_only', 'true');
      
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (festivalFilter !== 'all') params.append('festival_id', festivalFilter);
      if (departmentFilter !== 'all') params.append('department_id', departmentFilter);
      if (yearFilter !== 'all') params.append('payroll_year', yearFilter);
      if (searchTerm) params.append('search', searchTerm);

      const response = await fetch(buildApiUrl(`/api/v1/festival-advance/applications?${params}`), {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setApplications(data);
      }
    } catch (err) {
      console.error('Error fetching applications:', err);
    }
  };

  const fetchFestivals = async () => {
    try {
      const response = await fetch(buildApiUrl('/api/v1/festival-advance/festivals'), {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setFestivals(data);
      }
    } catch (err) {
      console.error('Error fetching festivals:', err);
    }
  };

  const fetchDepartments = async () => {
    try {
      const response = await fetch(buildApiUrl('/api/v1/departments'), {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setDepartments(data);
      }
    } catch (err) {
      console.error('Error fetching departments:', err);
    }
  };

  const handleCreateFestival = async (e) => {
    e.preventDefault();
    if (!festivalForm.festival_code.trim() || !festivalForm.festival_name.trim()) {
      setError('Festival Code and Name are required');
      return;
    }
    
    try {
      const response = await fetch(buildApiUrl('/api/v1/festival-advance/festivals'), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          festival_code: festivalForm.festival_code.toUpperCase().trim(),
          festival_name: festivalForm.festival_name.trim(),
          description: festivalForm.description.trim(),
          default_amount: parseFloat(festivalForm.default_amount || 12500),
          default_installments: parseInt(festivalForm.default_installments || 10, 10),
          is_active: true
        })
      });

      if (response.ok) {
        setSuccess('Festival created successfully');
        setTimeout(() => setSuccess(''), 3000);
        setShowCreateFestivalModal(false);
        setFestivalForm({
          festival_code: '',
          festival_name: '',
          description: '',
          default_amount: '12500.00',
          default_installments: '10'
        });
        fetchFestivals();
      } else {
        const errData = await response.json();
        setError(errData.detail || 'Failed to create festival');
        setTimeout(() => setError(''), 4000);
      }
    } catch (err) {
      console.error('Error creating festival:', err);
      setError('Failed to save festival master');
    }
  };

  const handleUpdateFestival = async (e) => {
    e.preventDefault();
    if (!editingFestival.festival_name.trim()) {
      setError('Festival Name is required');
      return;
    }

    try {
      const response = await fetch(buildApiUrl(`/api/v1/festival-advance/festivals/${editingFestival.id}`), {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          festival_name: editingFestival.festival_name.trim(),
          description: editingFestival.description ? editingFestival.description.trim() : null,
          default_amount: parseFloat(editingFestival.default_amount || 12500),
          default_installments: parseInt(editingFestival.default_installments || 10, 10),
          is_active: editingFestival.is_active
        })
      });

      if (response.ok) {
        setSuccess('Festival updated successfully');
        setTimeout(() => setSuccess(''), 3000);
        setShowEditFestivalModal(false);
        setEditingFestival(null);
        fetchFestivals();
      } else {
        const errData = await response.json();
        setError(errData.detail || 'Failed to update festival');
        setTimeout(() => setError(''), 4000);
      }
    } catch (err) {
      console.error('Error updating festival:', err);
      setError('Failed to update festival details');
    }
  };

  // Bulk Advance Handlers
  const handleBulkFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setBulkFile(file);
      setBulkPreviewData(null);
      setSelectedBulkRows({});
      setBulkBatchResult(null);
    }
  };

  const handleUploadAndPreview = async (e) => {
    e.preventDefault();
    if (!bulkFestivalId) {
      setError('Please select a festival before uploading.');
      return;
    }
    if (!bulkFile) {
      setError('Please select an Excel file (.xlsx or .xls) to upload.');
      return;
    }

    try {
      setBulkUploading(true);
      setError('');
      setSuccess('');
      setBulkBatchResult(null);

      const formData = new FormData();
      formData.append('festival_id', bulkFestivalId);
      formData.append('file', bulkFile);

      const response = await fetch(buildApiUrl('/api/v1/festival-advance/bulk-preview'), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        },
        body: formData
      });

      if (response.ok) {
        const data = await response.json();
        setBulkPreviewData(data);
        // Pre-select all valid rows by default
        const initialSelected = {};
        data.rows.forEach((r) => {
          if (r.is_valid) {
            initialSelected[r.row_number] = true;
          }
        });
        setSelectedBulkRows(initialSelected);
        setSuccess(`Parsed ${data.summary.total_rows} rows from ${data.file_title || 'Excel file'}.`);
        setTimeout(() => setSuccess(''), 4000);
      } else {
        const errData = await response.json();
        setError(errData.detail || 'Failed to parse Excel file.');
      }
    } catch (err) {
      console.error('Error previewing bulk file:', err);
      setError('Failed to process bulk Excel file. Please check server connection.');
    } finally {
      setBulkUploading(false);
    }
  };

  const handleToggleSelectAll = (filteredRows) => {
    const validFilteredRows = filteredRows.filter(r => r.is_valid);
    const allSelected = validFilteredRows.every(r => selectedBulkRows[r.row_number]);
    const updated = { ...selectedBulkRows };
    validFilteredRows.forEach(r => {
      updated[r.row_number] = !allSelected;
    });
    setSelectedBulkRows(updated);
  };

  const handleToggleRow = (rowNumber) => {
    setSelectedBulkRows(prev => ({
      ...prev,
      [rowNumber]: !prev[rowNumber]
    }));
  };

  const handleExecuteBulkProcess = async () => {
    if (!bulkPreviewData) return;

    const itemsToProcess = bulkPreviewData.rows
      .filter((r) => selectedBulkRows[r.row_number] && r.employee_id)
      .map((r) => ({
        row_number: r.row_number,
        employee_id: r.employee_id,
        amount: r.amount_raw,
        installments: bulkPreviewData.default_installments,
        remarks: `Bulk Excel Approval (${bulkPreviewData.file_title || bulkPreviewData.batch_reference})`
      }));

    if (itemsToProcess.length === 0) {
      setError('Please select at least one valid employee row to approve.');
      setShowConfirmBatchModal(false);
      return;
    }

    try {
      setBulkProcessing(true);
      setError('');
      setSuccess('');

      const response = await fetch(buildApiUrl('/api/v1/festival-advance/bulk-process'), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          festival_id: bulkPreviewData.festival_id,
          batch_reference: bulkPreviewData.batch_reference,
          custom_approval_date: bulkApprovalDate,
          items: itemsToProcess
        })
      });

      if (response.ok) {
        const result = await response.json();
        setBulkBatchResult(result);
        setShowConfirmBatchModal(false);
        setSuccess(`Batch ${result.batch_reference} approved successfully! Created ${result.success_count} advances.`);
        // Reload dashboard applications
        fetchApplications();
      } else {
        const errData = await response.json();
        setError(errData.detail || 'Failed to process bulk festival advances.');
      }
    } catch (err) {
      console.error('Error executing bulk process:', err);
      setError('Failed to execute bulk batch.');
    } finally {
      setBulkProcessing(false);
    }
  };

  const resetBulkForm = () => {
    setBulkFile(null);
    setBulkPreviewData(null);
    setSelectedBulkRows({});
    setBulkBatchResult(null);
    setBulkSearchTerm('');
    setBulkStatusFilter('all');
    setBulkSectionFilter('all');
  };

  const handleToggleFestivalStatus = async (festival) => {
    try {
      const newStatus = !festival.is_active;
      const response = await fetch(buildApiUrl(`/api/v1/festival-advance/festivals/${festival.id}/status`), {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ is_active: newStatus })
      });

      if (response.ok) {
        setSuccess(`Festival ${newStatus ? 'activated' : 'deactivated'} successfully`);
        setTimeout(() => setSuccess(''), 3000);
        fetchFestivals();
      } else {
        const errData = await response.json();
        setError(errData.detail || 'Failed to update status');
        setTimeout(() => setError(''), 4000);
      }
    } catch (err) {
      console.error('Error toggling status:', err);
      setError('Status toggle failed');
    }
  };

  const handleApplyFilters = () => {
    fetchApplications();
  };

  const handleResetFilters = () => {
    setSearchTerm('');
    setStatusFilter('all');
    setFestivalFilter('all');
    setDepartmentFilter('all');
    setYearFilter('all');
    // Delay slightly to allow state to clear before calling
    setTimeout(() => {
      fetchApplications();
    }, 50);
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'approved':
        return <span className="px-2.5 py-1 bg-emerald-50 text-emerald-700 rounded-full text-xs font-semibold border border-emerald-200">Approved</span>;
      case 'active':
        return <span className="px-2.5 py-1 bg-blue-50 text-blue-700 rounded-full text-xs font-semibold border border-blue-200">Active</span>;
      case 'completed':
        return <span className="px-2.5 py-1 bg-purple-50 text-purple-700 rounded-full text-xs font-semibold border border-purple-200">Completed</span>;
      default:
        return <span className="px-2.5 py-1 bg-gray-50 text-gray-700 rounded-full text-xs font-semibold border border-gray-200">{status}</span>;
    }
  };

  const getYearsList = () => {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let y = currentYear; y >= currentYear - 5; y--) {
      years.push(y);
    }
    return years;
  };

  if (!canView) {
    return (
      <div className="space-y-6 p-6 max-w-7xl mx-auto">
        <div className="bg-white rounded-2xl p-8 text-center text-slate-500 border border-gray-100 shadow-sm">
          You do not have permission to view the Festival Advance Registry.
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6 max-w-7xl mx-auto">
      {/* Premium Header */}
      <div className="flex justify-between items-center bg-gradient-to-r from-purple-900 to-indigo-900 p-6 rounded-2xl shadow-xl text-white">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Sparkles className="text-yellow-400" />
            Festival Advance Registry
          </h1>
          <p className="text-purple-200 text-sm mt-1">
            Read-only monitoring dashboard and configuration management for Festival Master
          </p>
        </div>
        {activeTab === 'festivals' && canAdd && (
          <button
            onClick={() => setShowCreateFestivalModal(true)}
            className="flex items-center gap-2 bg-yellow-500 hover:bg-yellow-400 text-purple-950 font-bold px-4 py-2.5 rounded-xl shadow-lg transition duration-200"
          >
            <Plus className="w-5 h-5" />
            Add New Festival
          </button>
        )}
      </div>

      {/* Notifications */}
      <AnimatePresence>
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="bg-rose-50 border border-rose-200 text-rose-700 px-4 py-3 rounded-xl flex items-center gap-2"
          >
            <AlertTriangle className="w-5 h-5 text-rose-500" />
            {error}
          </motion.div>
        )}
        {success && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="bg-emerald-50 border border-emerald-200 text-emerald-700 px-4 py-3 rounded-xl flex items-center gap-2"
          >
            <CheckCircle className="w-5 h-5 text-emerald-500" />
            {success}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Navigation Tabs */}
      <div className="border-b border-gray-200">
        <nav className="flex gap-8">
          {[
            { id: 'applications', label: 'Applications Dashboard', visible: true },
            { id: 'bulk', label: 'Bulk Festival Advance', visible: canAdd || canApprove },
            { id: 'festivals', label: 'Festival Master', visible: canAdd }
          ].filter(tab => tab.visible).map((tab) => (
            <button
              key={tab.id}
              onClick={() => {
                setActiveTab(tab.id);
                if (tab.id === 'applications') fetchApplications();
                if (tab.id === 'festivals') fetchFestivals();
              }}
              className={`pb-4 px-1 border-b-2 font-bold text-sm transition-all duration-200 ${
                activeTab === tab.id
                  ? 'border-purple-600 text-purple-700'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Applications Dashboard Tab */}
      {activeTab === 'applications' && (
        <div className="space-y-6">
          {/* Enhanced Search & Filters Panel */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
              {/* Search input */}
              <div className="lg:col-span-2 relative">
                <Search className="absolute left-3.5 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Search by code, name, application number..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-11 pr-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent transition"
                />
              </div>

              {/* Status Select */}
              <div>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent transition text-gray-700"
                >
                  <option value="all">All Statuses</option>
                  <option value="approved">Approved</option>
                  <option value="active">Active</option>
                  <option value="completed">Completed</option>
                </select>
              </div>

              {/* Festival Select */}
              <div>
                <select
                  value={festivalFilter}
                  onChange={(e) => setFestivalFilter(e.target.value)}
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent transition text-gray-700"
                >
                  <option value="all">All Festivals</option>
                  {festivals.map((fest) => (
                    <option key={fest.id} value={fest.id}>
                      {fest.festival_name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Department Select */}
              <div>
                <select
                  value={departmentFilter}
                  onChange={(e) => setDepartmentFilter(e.target.value)}
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent transition text-gray-700"
                >
                  <option value="all">All Departments</option>
                  {departments.map((dept) => (
                    <option key={dept.id} value={dept.id}>
                      {dept.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="flex justify-between items-center mt-4 pt-4 border-t border-gray-100">
              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold text-gray-600">Payroll Year:</span>
                <select
                  value={yearFilter}
                  onChange={(e) => setYearFilter(e.target.value)}
                  className="px-3 py-1.5 border border-gray-200 rounded-lg text-sm text-gray-700 focus:ring-2 focus:ring-purple-500"
                >
                  <option value="all">All Years</option>
                  {getYearsList().map((yr) => (
                    <option key={yr} value={yr}>
                      {yr}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex gap-3">
                <button
                  onClick={handleResetFilters}
                  className="px-4 py-2 border border-gray-200 rounded-xl hover:bg-gray-55 text-gray-600 font-semibold text-sm transition"
                >
                  Reset
                </button>
                <button
                  onClick={handleApplyFilters}
                  className="flex items-center gap-2 px-5 py-2 bg-purple-700 hover:bg-purple-800 text-white rounded-xl font-bold text-sm shadow-md transition"
                >
                  <Filter className="w-4 h-4" />
                  Filter Registry
                </button>
              </div>
            </div>
          </div>

          {/* Applications Data Table */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
            {loading ? (
              <div className="flex flex-col items-center justify-center py-20 gap-3">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-purple-700"></div>
                <p className="text-gray-500 text-sm font-semibold">Loading registry records...</p>
              </div>
            ) : applications.length === 0 ? (
              <div className="text-center py-20">
                <Award className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-bold text-gray-800">No Approved Applications Found</h3>
                <p className="text-gray-500 text-sm mt-1 max-w-md mx-auto">
                  There are no approved, active, or completed festival advances matching the chosen filters.
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm text-left">
                  <thead className="bg-gray-50 text-gray-700 font-semibold border-b border-gray-100">
                    <tr>
                      <th className="px-6 py-4">App. Number</th>
                      <th className="px-6 py-4">Employee</th>
                      <th className="px-6 py-4">Department</th>
                      <th className="px-6 py-4">Festival</th>
                      <th className="px-6 py-4 text-right">Applied / Approved</th>
                      <th className="px-6 py-4 text-center">Installments</th>
                      <th className="px-6 py-4">Date</th>
                      <th className="px-6 py-4">Status</th>
                      <th className="px-6 py-4 text-center">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 text-gray-700">
                    {applications.map((app) => (
                      <tr key={app.id} className="hover:bg-gray-50/50 transition">
                        <td className="px-6 py-4 font-bold text-purple-950">
                          {app.application_number}
                        </td>
                        <td className="px-6 py-4">
                          <div className="font-semibold text-gray-900">{app.employee?.full_name || 'N/A'}</div>
                          <div className="text-xs text-gray-500 font-medium">{app.employee?.emp_code || 'N/A'}</div>
                        </td>
                        <td className="px-6 py-4 font-medium text-gray-600">
                          {app.employee?.department_name || 'N/A'}
                        </td>
                        <td className="px-6 py-4 font-semibold text-gray-800">
                          {app.festival?.festival_name || 'N/A'}
                        </td>
                        <td className="px-6 py-4 text-right font-bold text-gray-900">
                          ₹{app.advance_amount?.toLocaleString() || 0}
                        </td>
                        <td className="px-6 py-4 text-center font-semibold text-gray-700">
                          {app.requested_installments} Months
                        </td>
                        <td className="px-6 py-4 text-gray-500 font-medium">
                          <div>Apply: {formatDate(app.application_date, 'N/A')}</div>
                          <div className="text-xs text-emerald-600">Appr: {formatDate(app.approved_date, '-')}</div>
                        </td>
                        <td className="px-6 py-4">
                          {getStatusBadge(app.status)}
                        </td>
                        <td className="px-6 py-4 text-center">
                          <button
                            onClick={() => {
                              setSelectedApplication(app);
                              setShowApplicationModal(true);
                            }}
                            className="p-2 bg-purple-50 text-purple-600 rounded-lg hover:bg-purple-100 transition shadow-sm"
                            title="View Details"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Festival Master Tab */}
      {activeTab === 'festivals' && (
        <div className="space-y-6">
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
            {loading ? (
              <div className="flex flex-col items-center justify-center py-20 gap-3">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-purple-700"></div>
                <p className="text-gray-500 text-sm font-semibold">Loading festival master list...</p>
              </div>
            ) : festivals.length === 0 ? (
              <div className="text-center py-20">
                <Calendar className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-bold text-gray-800">No Festivals Configured</h3>
                <p className="text-gray-500 text-sm mt-1">
                  Click 'Add New Festival' to configure a festival code in the system.
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm text-left">
                  <thead className="bg-gray-50 text-gray-700 font-semibold border-b border-gray-100">
                    <tr>
                      <th className="px-6 py-4">Festival Code</th>
                      <th className="px-6 py-4">Festival Name</th>
                      <th className="px-6 py-4">Standard Amount</th>
                      <th className="px-6 py-4">Recovery Tenure</th>
                      <th className="px-6 py-4">Description</th>
                      <th className="px-6 py-4">Status</th>
                      <th className="px-6 py-4 text-center">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 text-gray-700">
                    {festivals.map((festival) => (
                      <tr key={festival.id} className="hover:bg-gray-50/50 transition">
                        <td className="px-6 py-4 font-bold text-purple-950">
                          {festival.festival_code}
                        </td>
                        <td className="px-6 py-4 font-semibold text-gray-900">
                          {festival.festival_name}
                        </td>
                        <td className="px-6 py-4 font-bold text-slate-800">
                          ₹{parseFloat(festival.default_amount || 12500).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                        </td>
                        <td className="px-6 py-4 font-medium text-slate-700">
                          {festival.default_installments || 10} Months
                        </td>
                        <td className="px-6 py-4 text-gray-500 font-medium max-w-xs truncate">
                          {festival.description || 'No description provided'}
                        </td>
                        <td className="px-6 py-4">
                          <span
                            className={`px-2.5 py-1 text-xs font-semibold rounded-full border ${
                              festival.is_active
                                ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                                : 'bg-rose-50 text-rose-700 border-rose-200'
                            }`}
                          >
                            {festival.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          {canAdd && (
                            <div className="flex items-center justify-center gap-3">
                              {/* Edit Action */}
                              <button
                                onClick={() => {
                                  setEditingFestival(festival);
                                  setShowEditFestivalModal(true);
                                }}
                                className="p-2 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition shadow-sm"
                                title="Edit Festival"
                              >
                                <Edit3 className="w-4 h-4" />
                              </button>

                              {/* Activate/Deactivate Action */}
                              <button
                                onClick={() => handleToggleFestivalStatus(festival)}
                                className={`p-2 rounded-lg transition shadow-sm ${
                                  festival.is_active
                                    ? 'bg-rose-50 text-rose-600 hover:bg-rose-100'
                                    : 'bg-emerald-50 text-emerald-600 hover:bg-emerald-100'
                                }`}
                                title={festival.is_active ? 'Deactivate Festival' : 'Activate Festival'}
                              >
                                {festival.is_active ? (
                                  <ToggleRight className="w-5 h-5" />
                                ) : (
                                  <ToggleLeft className="w-5 h-5" />
                                )}
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Bulk Festival Advance Tab */}
      {activeTab === 'bulk' && (
        <div className="space-y-6">
          {/* Top Info Banner */}
          <div className="bg-gradient-to-r from-indigo-50 via-purple-50 to-pink-50 border border-purple-200 rounded-2xl p-5 shadow-xs">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div className="flex items-start gap-3">
                <div className="p-2.5 bg-purple-700 text-white rounded-xl shadow-xs shrink-0 mt-0.5">
                  <FileSpreadsheet className="w-6 h-6" />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                    Official Excel Disbursement Importer
                    <span className="px-2.5 py-0.5 text-xs bg-purple-100 text-purple-800 font-semibold rounded-full border border-purple-200">
                      Auto-Approve Batch
                    </span>
                  </h2>
                  <p className="text-sm text-gray-600 mt-0.5">
                    Upload official board festival disbursement lists (containing Section headers, Code No., Name, A/C No., and Amount).
                    The system validates HRMS active status, outstanding balances, and bank accounts, and batch-activates advances directly.
                  </p>
                </div>
              </div>
              <div className="shrink-0 bg-white/80 border border-purple-200 rounded-xl p-3 text-xs text-purple-900 font-medium max-w-xs shadow-2xs">
                <p className="font-bold flex items-center gap-1.5 text-purple-950 mb-1">
                  <Calendar className="w-4 h-4 text-purple-700" />
                  Recovery Start Timing
                </p>
                First recovery installment is deducted in the <strong className="text-purple-700">same calendar month</strong> of the approved date.
              </div>
            </div>
          </div>

          {/* Batch Result View (Shown After Successful Batch Processing) */}
          {bulkBatchResult ? (
            <motion.div
              initial={{ opacity: 0, scale: 0.98 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-white rounded-2xl shadow-sm border border-emerald-200 p-8 space-y-6"
            >
              <div className="flex items-center gap-4 border-b border-gray-100 pb-6">
                <div className="w-14 h-14 bg-emerald-100 text-emerald-700 rounded-2xl flex items-center justify-center shrink-0 shadow-xs">
                  <CheckCircle className="w-8 h-8" />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <span className="px-2.5 py-0.5 bg-emerald-100 text-emerald-800 rounded-full text-xs font-bold uppercase tracking-wider">
                      Batch Approved
                    </span>
                    <span className="text-xs text-gray-500 font-mono">
                      Ref: {bulkBatchResult.batch_reference}
                    </span>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mt-1">
                    Festival Advance Batch Successfully Processed!
                  </h3>
                  <p className="text-sm text-gray-600">
                    Advances have been activated and repayment schedules generated for all confirmed employees.
                  </p>
                </div>
              </div>

              {/* KPI Results */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4">
                  <p className="text-xs font-bold text-emerald-700 uppercase tracking-wider">Approved & Activated</p>
                  <p className="text-2xl font-black text-emerald-900 mt-1">{bulkBatchResult.success_count}</p>
                  <p className="text-xs text-emerald-600 mt-0.5">of {bulkBatchResult.total_requested} requested</p>
                </div>
                <div className="bg-purple-50 border border-purple-200 rounded-xl p-4">
                  <p className="text-xs font-bold text-purple-700 uppercase tracking-wider">Total Disbursed</p>
                  <p className="text-2xl font-black text-purple-950 mt-1">
                    ₹{parseFloat(bulkBatchResult.total_amount_approved).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                  </p>
                  <p className="text-xs text-purple-600 mt-0.5">{bulkBatchResult.festival_name}</p>
                </div>
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                  <p className="text-xs font-bold text-blue-700 uppercase tracking-wider">Recovery Starts</p>
                  <p className="text-2xl font-black text-blue-950 mt-1">
                    {new Date(bulkBatchResult.recovery_start_year, bulkBatchResult.recovery_start_month - 1, 1).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
                  </p>
                  <p className="text-xs text-blue-600 mt-0.5">Same-month payroll deduction</p>
                </div>
                <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
                  <p className="text-xs font-bold text-gray-700 uppercase tracking-wider">Failed / Skipped</p>
                  <p className="text-2xl font-black text-gray-900 mt-1">{bulkBatchResult.failure_count}</p>
                  <p className="text-xs text-gray-500 mt-0.5">Due to live checks</p>
                </div>
              </div>

              {/* Results table snippet */}
              <div className="border border-gray-200 rounded-xl overflow-hidden">
                <div className="p-3 bg-gray-50 border-b border-gray-200 font-bold text-sm text-gray-700 flex justify-between items-center">
                  <span>Batch Execution Details</span>
                  <span className="text-xs font-normal text-gray-500">
                    Showing all {bulkBatchResult.results.length} processed items
                  </span>
                </div>
                <div className="max-h-72 overflow-y-auto divide-y divide-gray-100 text-sm">
                  {bulkBatchResult.results.map((res, idx) => (
                    <div key={idx} className="p-3 flex items-center justify-between hover:bg-gray-50 transition">
                      <div className="flex items-center gap-3">
                        <span className={`w-2.5 h-2.5 rounded-full ${res.success ? 'bg-emerald-500' : 'bg-rose-500'}`} />
                        <div>
                          <p className="font-bold text-gray-900">
                            {res.employee_name || `Employee #${res.employee_id}`}
                            <span className="ml-2 font-mono text-xs text-gray-500">({res.employee_code || '—'})</span>
                          </p>
                          <p className="text-xs text-gray-500">
                            {res.success ? (
                              <span className="text-emerald-700 font-medium">Application No: {res.application_number}</span>
                            ) : (
                              <span className="text-rose-600 font-medium">{res.error_message}</span>
                            )}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        {res.success && (
                          <span className="font-bold text-gray-900">
                            ₹{parseFloat(res.amount).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex justify-between items-center pt-4 border-t border-gray-100">
                <button
                  type="button"
                  onClick={resetBulkForm}
                  className="px-5 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-800 font-bold rounded-xl transition text-sm flex items-center gap-2"
                >
                  <RefreshCw className="w-4 h-4" />
                  Process Another Batch
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setActiveTab('applications');
                    fetchApplications();
                  }}
                  className="px-6 py-2.5 bg-purple-700 hover:bg-purple-800 text-white font-bold rounded-xl shadow-md transition text-sm flex items-center gap-2"
                >
                  View in Applications Dashboard
                  <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </motion.div>
          ) : !bulkPreviewData ? (
            /* Step 1: Upload Form */
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 space-y-6">
              <form onSubmit={handleUploadAndPreview} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Select Festival */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                      Target Festival *
                    </label>
                    <select
                      value={bulkFestivalId}
                      onChange={(e) => setBulkFestivalId(e.target.value)}
                      required
                      className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent transition text-sm bg-white font-medium"
                    >
                      <option value="">-- Choose Active Festival --</option>
                      {festivals.filter(f => f.is_active).map((f) => (
                        <option key={f.id} value={f.id}>
                          {f.festival_name} ({f.festival_code}) — Standard: ₹{parseFloat(f.default_amount || 12500).toLocaleString('en-IN')}
                        </option>
                      ))}
                    </select>
                    {bulkFestivalId && (() => {
                      const sel = festivals.find(f => f.id === parseInt(bulkFestivalId, 10));
                      if (!sel) return null;
                      return (
                        <p className="text-xs text-purple-700 font-semibold mt-1.5 flex items-center gap-1.5">
                          <Award className="w-3.5 h-3.5" />
                          Configured Standard: ₹{parseFloat(sel.default_amount || 12500).toLocaleString('en-IN')} in {sel.default_installments || 10} monthly installments
                        </p>
                      );
                    })()}
                  </div>

                  {/* Approval Date */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                      Batch Approval & Activation Date *
                    </label>
                    <input
                      type="date"
                      value={bulkApprovalDate}
                      onChange={(e) => setBulkApprovalDate(e.target.value)}
                      required
                      className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent transition text-sm font-medium"
                    />
                    {bulkApprovalDate && (
                      <p className="text-xs text-indigo-700 font-semibold mt-1.5 flex items-center gap-1.5">
                        <Calendar className="w-3.5 h-3.5" />
                        First deduction: {new Date(bulkApprovalDate + 'T00:00:00').toLocaleDateString('en-US', { month: 'long', year: 'numeric' })} payroll
                      </p>
                    )}
                  </div>
                </div>

                {/* File Dropzone */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                    Official Excel Disbursement File *
                  </label>
                  <div className="border-2 border-dashed border-purple-200 rounded-2xl p-8 hover:border-purple-400 transition text-center bg-purple-50/20 relative group">
                    <input
                      id="bulk-file-input"
                      type="file"
                      accept=".xlsx, .xls"
                      onChange={handleBulkFileChange}
                      className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                    />
                    <div className="flex flex-col items-center justify-center pointer-events-none">
                      <div className="p-3 bg-purple-100 text-purple-700 rounded-2xl mb-3 group-hover:scale-105 transition transform">
                        <UploadCloud className="w-8 h-8" />
                      </div>
                      <p className="text-sm font-bold text-gray-800">
                        {bulkFile ? bulkFile.name : 'Click or drag official Excel sheet here'}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        Supported: .xlsx, .xls (Official disbursement list containing Code No., Name, A/C No., AMOUNT)
                      </p>
                      {bulkFile && (
                        <span className="mt-3 inline-flex items-center gap-1 px-3 py-1 bg-purple-100 text-purple-800 rounded-full text-xs font-semibold">
                          <Check className="w-3.5 h-3.5" />
                          {(bulkFile.size / 1024).toFixed(1)} KB selected
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Upload Button */}
                <div className="flex justify-end pt-2">
                  <button
                    type="submit"
                    disabled={bulkUploading || !bulkFestivalId || !bulkFile}
                    className={`px-7 py-3 rounded-xl font-bold text-sm shadow-md transition flex items-center gap-2 ${
                      bulkUploading || !bulkFestivalId || !bulkFile
                        ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                        : 'bg-purple-700 hover:bg-purple-800 text-white shadow-purple-200 hover:shadow-lg'
                    }`}
                  >
                    {bulkUploading ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        Parsing & Validating Excel...
                      </>
                    ) : (
                      <>
                        <FileCheck className="w-5 h-5" />
                        Parse & Preview Disbursement List
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          ) : (
            /* Step 2: Preview & Validation Table */
            <div className="space-y-6">
              {/* Batch Meta Header */}
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="px-2.5 py-0.5 bg-indigo-50 text-indigo-700 rounded-full text-xs font-bold border border-indigo-200">
                      Disbursement Preview
                    </span>
                    <span className="text-xs text-gray-500 font-mono">
                      {bulkPreviewData.batch_reference}
                    </span>
                  </div>
                  <h3 className="text-xl font-black text-gray-900 mt-1 flex items-center gap-2">
                    {bulkPreviewData.festival_name}
                    {bulkPreviewData.file_title && (
                      <span className="text-sm font-semibold text-gray-500 font-normal">
                        ({bulkPreviewData.file_title})
                      </span>
                    )}
                  </h3>
                  <p className="text-xs text-gray-500 mt-0.5">
                    Standard Amount: ₹{parseFloat(bulkPreviewData.default_amount).toLocaleString('en-IN')} | Tenure: {bulkPreviewData.default_installments} Months | Approval Date: {formatDate(bulkApprovalDate)} (Recovery starts {new Date(bulkApprovalDate + 'T00:00:00').toLocaleDateString('en-US', { month: 'long', year: 'numeric' })})
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={resetBulkForm}
                    className="px-4 py-2 border border-gray-200 text-gray-700 hover:bg-gray-100 font-bold rounded-xl text-xs transition"
                  >
                    Re-upload File
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowConfirmBatchModal(true)}
                    disabled={Object.values(selectedBulkRows).filter(Boolean).length === 0}
                    className={`px-5 py-2 rounded-xl text-xs font-bold transition shadow-md flex items-center gap-2 ${
                      Object.values(selectedBulkRows).filter(Boolean).length === 0
                        ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                        : 'bg-emerald-600 hover:bg-emerald-700 text-white shadow-emerald-200'
                    }`}
                  >
                    <CheckCircle className="w-4 h-4" />
                    Approve Selected ({Object.values(selectedBulkRows).filter(Boolean).length})
                  </button>
                </div>
              </div>

              {/* KPI Cards */}
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                <div className="bg-white border border-gray-100 rounded-2xl p-4 shadow-2xs">
                  <p className="text-xs font-bold text-gray-500 uppercase tracking-wider">Total Rows</p>
                  <p className="text-2xl font-black text-gray-900 mt-1">{bulkPreviewData.summary.total_rows}</p>
                  <p className="text-xs text-gray-400 mt-0.5">In uploaded sheet</p>
                </div>
                <div className="bg-white border border-emerald-100 rounded-2xl p-4 shadow-2xs">
                  <p className="text-xs font-bold text-emerald-600 uppercase tracking-wider">Eligible & Ready</p>
                  <p className="text-2xl font-black text-emerald-700 mt-1">{bulkPreviewData.summary.valid_count}</p>
                  <p className="text-xs text-emerald-600 mt-0.5">Passed all rules</p>
                </div>
                <div className="bg-white border border-rose-100 rounded-2xl p-4 shadow-2xs">
                  <p className="text-xs font-bold text-rose-600 uppercase tracking-wider">Ineligible / Errors</p>
                  <p className="text-2xl font-black text-rose-700 mt-1">{bulkPreviewData.summary.error_count}</p>
                  <p className="text-xs text-rose-600 mt-0.5">Cannot be approved</p>
                </div>
                <div className="bg-white border border-amber-100 rounded-2xl p-4 shadow-2xs">
                  <p className="text-xs font-bold text-amber-600 uppercase tracking-wider">Discrepancies</p>
                  <p className="text-2xl font-black text-amber-700 mt-1">{bulkPreviewData.summary.warning_count}</p>
                  <p className="text-xs text-amber-600 mt-0.5">Account / Amount diff</p>
                </div>
                <div className="bg-gradient-to-br from-purple-900 to-indigo-900 text-white rounded-2xl p-4 shadow-xs">
                  <p className="text-xs font-bold text-purple-200 uppercase tracking-wider">Selected Total</p>
                  <p className="text-xl font-black mt-1">
                    ₹{bulkPreviewData.rows
                      .filter(r => selectedBulkRows[r.row_number])
                      .reduce((sum, r) => sum + (parseFloat(r.amount_raw) || 0), 0)
                      .toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                  </p>
                  <p className="text-xs text-purple-200 mt-0.5">
                    {Object.values(selectedBulkRows).filter(Boolean).length} employees selected
                  </p>
                </div>
              </div>

              {/* Filters & Search Toolbar */}
              <div className="bg-white rounded-2xl border border-gray-100 p-4 shadow-2xs space-y-3">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div className="flex flex-1 items-center gap-3">
                    {/* Search */}
                    <div className="relative flex-1 max-w-md">
                      <Search className="w-4 h-4 text-gray-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
                      <input
                        type="text"
                        value={bulkSearchTerm}
                        onChange={(e) => setBulkSearchTerm(e.target.value)}
                        placeholder="Search code, name, or section..."
                        className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-xl text-xs focus:ring-2 focus:ring-purple-500 focus:border-transparent transition"
                      />
                    </div>

                    {/* Section filter */}
                    {bulkPreviewData.summary.sections?.length > 0 && (
                      <select
                        value={bulkSectionFilter}
                        onChange={(e) => setBulkSectionFilter(e.target.value)}
                        className="px-3 py-2 border border-gray-200 rounded-xl text-xs bg-white text-gray-700 font-medium"
                      >
                        <option value="all">All Sections ({bulkPreviewData.summary.sections.length})</option>
                        {bulkPreviewData.summary.sections.map((s, idx) => (
                          <option key={idx} value={s}>{s}</option>
                        ))}
                      </select>
                    )}
                  </div>

                  {/* Status Pills */}
                  <div className="flex items-center gap-2">
                    {[
                      { id: 'all', label: `All (${bulkPreviewData.summary.total_rows})` },
                      { id: 'valid', label: `Ready (${bulkPreviewData.summary.valid_count})` },
                      { id: 'warnings', label: `Warnings (${bulkPreviewData.summary.warning_count})` },
                      { id: 'errors', label: `Errors (${bulkPreviewData.summary.error_count})` }
                    ].map((pill) => (
                      <button
                        key={pill.id}
                        type="button"
                        onClick={() => setBulkStatusFilter(pill.id)}
                        className={`px-3 py-1.5 rounded-lg text-xs font-bold transition ${
                          bulkStatusFilter === pill.id
                            ? 'bg-purple-700 text-white shadow-xs'
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                      >
                        {pill.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Preview Table */}
              <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                {(() => {
                  const filteredRows = bulkPreviewData.rows.filter((row) => {
                    if (bulkSearchTerm) {
                      const term = bulkSearchTerm.toLowerCase();
                      const code = (row.employee_code || row.emp_code_raw || '').toLowerCase();
                      const name = (row.employee_name || row.emp_name_raw || '').toLowerCase();
                      const sec = (row.section_name || '').toLowerCase();
                      if (!code.includes(term) && !name.includes(term) && !sec.includes(term)) {
                        return false;
                      }
                    }
                    if (bulkSectionFilter !== 'all' && row.section_name !== bulkSectionFilter) {
                      return false;
                    }
                    if (bulkStatusFilter === 'valid') return row.is_valid;
                    if (bulkStatusFilter === 'errors') return !row.is_valid;
                    if (bulkStatusFilter === 'warnings') return row.is_account_mismatch || row.is_amount_mismatch;
                    return true;
                  });

                  const validFilteredRows = filteredRows.filter(r => r.is_valid);
                  const isAllFilteredSelected = validFilteredRows.length > 0 && validFilteredRows.every(r => selectedBulkRows[r.row_number]);

                  return (
                    <div className="overflow-x-auto">
                      <table className="w-full text-xs text-left">
                        <thead className="bg-gray-50 text-gray-700 font-semibold border-b border-gray-100">
                          <tr>
                            <th className="px-4 py-3.5 w-10 text-center">
                              <input
                                type="checkbox"
                                checked={isAllFilteredSelected}
                                onChange={() => handleToggleSelectAll(filteredRows)}
                                disabled={validFilteredRows.length === 0}
                                className="rounded text-purple-600 focus:ring-purple-500 border-gray-300 h-4 w-4"
                              />
                            </th>
                            <th className="px-4 py-3.5">Row / Section</th>
                            <th className="px-4 py-3.5">Code No.</th>
                            <th className="px-4 py-3.5">Employee Name</th>
                            <th className="px-4 py-3.5">Department / Designation</th>
                            <th className="px-4 py-3.5">Bank Account No.</th>
                            <th className="px-4 py-3.5 text-right">Amount (₹)</th>
                            <th className="px-4 py-3.5">Validation Status</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100 text-gray-700">
                          {filteredRows.length === 0 ? (
                            <tr>
                              <td colSpan="8" className="text-center py-12 text-gray-500">
                                No records match the current filters.
                              </td>
                            </tr>
                          ) : (
                            filteredRows.map((row) => {
                              const isChecked = !!selectedBulkRows[row.row_number];
                              return (
                                <tr
                                  key={row.row_number}
                                  className={`transition ${
                                    !row.is_valid
                                      ? 'bg-rose-50/40 opacity-90'
                                      : isChecked
                                      ? 'bg-purple-50/30'
                                      : 'hover:bg-gray-50/60'
                                  }`}
                                >
                                  {/* Checkbox */}
                                  <td className="px-4 py-3 text-center">
                                    <input
                                      type="checkbox"
                                      checked={isChecked}
                                      disabled={!row.is_valid}
                                      onChange={() => handleToggleRow(row.row_number)}
                                      className="rounded text-purple-600 focus:ring-purple-500 border-gray-300 h-4 w-4 disabled:opacity-30"
                                    />
                                  </td>

                                  {/* Row & Section */}
                                  <td className="px-4 py-3 font-medium">
                                    <span className="text-gray-400 font-mono mr-1.5">#{row.row_number}</span>
                                    {row.section_name ? (
                                      <span className="px-2 py-0.5 bg-gray-100 text-gray-700 rounded-md text-[11px] font-semibold">
                                        {row.section_name}
                                      </span>
                                    ) : (
                                      <span className="text-gray-400 italic text-[11px]">General</span>
                                    )}
                                  </td>

                                  {/* Code */}
                                  <td className="px-4 py-3 font-bold text-gray-900 font-mono">
                                    {row.emp_code_raw}
                                  </td>

                                  {/* Name */}
                                  <td className="px-4 py-3 font-semibold text-gray-900">
                                    {row.employee_name || row.emp_name_raw || '—'}
                                    {row.emp_name_raw && row.employee_name && row.emp_name_raw.toLowerCase() !== row.employee_name.toLowerCase() && (
                                      <span className="block text-[11px] text-gray-400 font-normal italic">
                                        Excel: {row.emp_name_raw}
                                      </span>
                                    )}
                                  </td>

                                  {/* Department & Designation */}
                                  <td className="px-4 py-3 text-gray-600">
                                    <div>{row.department_name || '—'}</div>
                                    <div className="text-[11px] text-gray-400">{row.designation_name || '—'}</div>
                                  </td>

                                  {/* Bank Account */}
                                  <td className="px-4 py-3 font-mono">
                                    <div className="font-semibold text-gray-800">
                                      {row.masked_bank_account || '••••'}
                                    </div>
                                    {row.is_account_mismatch && (
                                      <span className="inline-flex items-center gap-1 text-[10px] text-amber-700 bg-amber-50 px-1.5 py-0.5 rounded border border-amber-200 mt-0.5">
                                        <AlertTriangle className="w-2.5 h-2.5" />
                                        Excel: {row.account_number_raw}
                                      </span>
                                    )}
                                  </td>

                                  {/* Amount */}
                                  <td className="px-4 py-3 text-right font-bold text-gray-900">
                                    <div>
                                      ₹{parseFloat(row.amount_raw).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                                    </div>
                                    {row.is_amount_mismatch && (
                                      <span className="inline-block text-[10px] text-rose-700 bg-rose-50 px-1.5 py-0.5 rounded border border-rose-200 mt-0.5">
                                        Differs (₹{parseFloat(bulkPreviewData.default_amount).toLocaleString('en-IN')})
                                      </span>
                                    )}
                                  </td>

                                  {/* Status */}
                                  <td className="px-4 py-3">
                                    {row.is_valid ? (
                                      <div>
                                        <span className="inline-flex items-center gap-1 px-2.5 py-0.5 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-full font-bold text-[11px]">
                                          <Check className="w-3 h-3" />
                                          Ready to Approve
                                        </span>
                                        {row.validation_messages.length > 0 && (
                                          <p className="text-[10px] text-amber-700 mt-0.5">
                                            {row.validation_messages[0]}
                                          </p>
                                        )}
                                      </div>
                                    ) : (
                                      <div>
                                        <span className="inline-flex items-center gap-1 px-2.5 py-0.5 bg-rose-50 text-rose-700 border border-rose-200 rounded-full font-bold text-[11px]">
                                          <XCircle className="w-3 h-3" />
                                          {row.status === 'active_advance_exists' ? 'Outstanding Advance' :
                                           row.status === 'duplicate_in_file' ? 'Duplicate in File' :
                                           row.status === 'employee_not_found' ? 'Not in HRMS' :
                                           row.status === 'inactive_employee' ? 'Inactive Employee' : 'Ineligible'}
                                        </span>
                                        {row.validation_messages.length > 0 && (
                                          <p className="text-[10px] text-rose-700 mt-0.5 max-w-xs">
                                            {row.validation_messages[0]}
                                          </p>
                                        )}
                                      </div>
                                    )}
                                  </td>
                                </tr>
                              );
                            })
                          )}
                        </tbody>
                      </table>
                    </div>
                  );
                })()}

                {/* Sticky Action Bar */}
                <div className="p-4 bg-gray-50 border-t border-gray-100 flex flex-col md:flex-row md:items-center justify-between gap-3">
                  <div className="text-xs text-gray-600 font-medium flex items-center gap-2">
                    <ShieldCheck className="w-4 h-4 text-emerald-600" />
                    <span>
                      Selected <strong className="text-purple-900 font-bold">{Object.values(selectedBulkRows).filter(Boolean).length}</strong> of {bulkPreviewData.summary.valid_count} eligible employees
                    </span>
                  </div>
                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      onClick={resetBulkForm}
                      className="px-4 py-2 border border-gray-200 text-gray-600 font-semibold rounded-xl hover:bg-gray-100 transition text-xs"
                    >
                      Reset / Cancel
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowConfirmBatchModal(true)}
                      disabled={Object.values(selectedBulkRows).filter(Boolean).length === 0}
                      className={`px-6 py-2.5 font-bold rounded-xl shadow-md transition text-xs flex items-center gap-2 ${
                        Object.values(selectedBulkRows).filter(Boolean).length === 0
                          ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                          : 'bg-purple-700 hover:bg-purple-800 text-white shadow-purple-200'
                      }`}
                    >
                      <CheckCircle className="w-4 h-4" />
                      Batch Approve Advances ({Object.values(selectedBulkRows).filter(Boolean).length})
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Confirmation Modal for Bulk Approval */}
      {showConfirmBatchModal && bulkPreviewData && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 backdrop-blur-xs">
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full mx-4 overflow-hidden border border-gray-100 transform transition-all duration-300">
            <div className="p-5 bg-gradient-to-r from-purple-900 to-indigo-900 text-white flex items-center justify-between">
              <h2 className="text-lg font-bold flex items-center gap-2">
                <ShieldCheck className="w-5 h-5 text-emerald-400" />
                Confirm Bulk Advance Approval
              </h2>
              <button
                onClick={() => setShowConfirmBatchModal(false)}
                className="p-1 hover:bg-purple-800 text-purple-100 rounded-lg transition"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 space-y-4 text-sm">
              <p className="text-gray-600">
                You are about to batch-approve festival advance records directly from the official Excel disbursement list.
              </p>
              <div className="bg-purple-50/60 border border-purple-100 rounded-xl p-4 space-y-2 text-xs">
                <div className="flex justify-between">
                  <span className="text-gray-500 font-medium">Target Festival:</span>
                  <span className="font-bold text-gray-900">{bulkPreviewData.festival_name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500 font-medium">Batch Reference:</span>
                  <span className="font-mono font-semibold text-purple-950">{bulkPreviewData.batch_reference}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500 font-medium">Employees to Approve:</span>
                  <span className="font-bold text-emerald-700">{Object.values(selectedBulkRows).filter(Boolean).length}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500 font-medium">Total Disbursement Amount:</span>
                  <span className="font-bold text-gray-900">
                    ₹{bulkPreviewData.rows
                      .filter(r => selectedBulkRows[r.row_number])
                      .reduce((sum, r) => sum + (parseFloat(r.amount_raw) || 0), 0)
                      .toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500 font-medium">Approval Date:</span>
                  <span className="font-bold text-gray-900">{bulkApprovalDate}</span>
                </div>
                <div className="flex justify-between border-t border-purple-200/60 pt-2 mt-2">
                  <span className="text-indigo-900 font-bold">First Recovery Month:</span>
                  <span className="font-bold text-indigo-700">
                    {new Date(bulkApprovalDate + 'T00:00:00').toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                  </span>
                </div>
              </div>
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 text-xs text-blue-800 flex items-start gap-2">
                <Info className="w-4 h-4 text-blue-600 shrink-0 mt-0.5" />
                <span>
                  Advances will be immediately activated with repayment schedules starting in <strong>{new Date(bulkApprovalDate + 'T00:00:00').toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}</strong> payroll. No further manual approvals are required.
                </span>
              </div>
            </div>
            <div className="p-4 bg-gray-50 border-t border-gray-100 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setShowConfirmBatchModal(false)}
                disabled={bulkProcessing}
                className="px-4 py-2 border border-gray-200 text-gray-600 font-semibold rounded-xl hover:bg-gray-100 transition text-sm"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleExecuteBulkProcess}
                disabled={bulkProcessing}
                className="px-6 py-2 bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-xl shadow-md transition text-sm flex items-center gap-2"
              >
                {bulkProcessing ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Executing Batch Approval...
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-4 h-4" />
                    Confirm & Activate Advances
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Read-only Application Detail Modal */}
      {showApplicationModal && selectedApplication && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 backdrop-blur-xs">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full mx-4 overflow-hidden border border-gray-100 transform transition-all duration-300">
            <div className="p-6 bg-gradient-to-r from-purple-900 to-indigo-900 text-white flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold">Advance Details Monitor</h2>
                <p className="text-xs text-purple-200 mt-1">Application No: {selectedApplication.application_number}</p>
              </div>
              <button
                onClick={() => setShowApplicationModal(false)}
                className="p-1.5 bg-purple-800 hover:bg-purple-700 text-purple-100 rounded-lg transition"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 space-y-6">
              <div className="grid grid-cols-2 gap-6 bg-gray-50 p-4 rounded-xl border border-gray-100 text-sm">
                <div>
                  <p className="text-gray-500 font-medium">Employee Name</p>
                  <p className="font-bold text-gray-900 text-base mt-0.5">{selectedApplication.employee?.full_name || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-gray-500 font-medium">Employee Code</p>
                  <p className="font-bold text-gray-900 text-base mt-0.5">{selectedApplication.employee?.emp_code || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-gray-500 font-medium">Department</p>
                  <p className="font-semibold text-gray-800 mt-0.5">{selectedApplication.employee?.department_name || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-gray-500 font-medium">Festival Reference</p>
                  <p className="font-semibold text-gray-800 mt-0.5">{selectedApplication.festival?.festival_name || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-gray-500 font-medium">Granted Advance Amount</p>
                  <p className="font-extrabold text-purple-950 text-base mt-0.5">₹{selectedApplication.advance_amount?.toLocaleString()}</p>
                </div>
                <div>
                  <p className="text-gray-500 font-medium">Deduction Recovery Duration</p>
                  <p className="font-semibold text-gray-800 mt-0.5">{selectedApplication.requested_installments} Months</p>
                </div>
                <div>
                  <p className="text-gray-500 font-medium">Monthly Installment Amount</p>
                  <p className="font-bold text-gray-950 mt-0.5">₹{selectedApplication.monthly_installment?.toLocaleString()}/month</p>
                </div>
                <div>
                  <p className="text-gray-500 font-medium">Outstanding Balance</p>
                  <p className="font-bold text-rose-600 mt-0.5">₹{selectedApplication.outstanding_balance?.toLocaleString() || 0}</p>
                </div>
                <div>
                  <p className="text-gray-500 font-medium">Application Date</p>
                  <p className="font-medium text-gray-800 mt-0.5">{formatDate(selectedApplication.application_date, 'N/A')}</p>
                </div>
                <div>
                  <p className="text-gray-500 font-medium">Approval Date</p>
                  <p className="font-medium text-emerald-600 mt-0.5">{formatDate(selectedApplication.approved_date, 'N/A')}</p>
                </div>
              </div>
              {selectedApplication.remarks && (
                <div>
                  <p className="text-sm font-semibold text-gray-600 mb-1">Employee Application Remarks</p>
                  <div className="bg-gray-50 border border-gray-150 p-3 rounded-lg text-sm text-gray-800 italic">
                    "{selectedApplication.remarks}"
                  </div>
                </div>
              )}
            </div>
            <div className="p-4 bg-gray-50 border-t border-gray-100 flex justify-end">
              <button
                onClick={() => setShowApplicationModal(false)}
                className="px-5 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 font-bold rounded-xl transition text-sm"
              >
                Close Monitor
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Festival Modal */}
      {showCreateFestivalModal && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 backdrop-blur-xs">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 overflow-hidden border border-gray-100 transform transition-all duration-300">
            <div className="p-5 bg-gradient-to-r from-purple-900 to-indigo-900 text-white flex items-center justify-between">
              <h2 className="text-lg font-bold">Create Festival Master</h2>
              <button
                onClick={() => setShowCreateFestivalModal(false)}
                className="p-1 hover:bg-purple-800 text-purple-100 rounded-lg transition"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleCreateFestival}>
              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">
                    Festival Code *
                  </label>
                  <input
                    type="text"
                    value={festivalForm.festival_code}
                    onChange={(e) => setFestivalForm({ ...festivalForm, festival_code: e.target.value.toUpperCase() })}
                    placeholder="e.g., DIWALI"
                    required
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent transition"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">
                    Festival Name *
                  </label>
                  <input
                    type="text"
                    value={festivalForm.festival_name}
                    onChange={(e) => setFestivalForm({ ...festivalForm, festival_name: e.target.value })}
                    placeholder="e.g., Diwali"
                    required
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent transition"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">
                      Standard Amount (₹) *
                    </label>
                    <input
                      type="number"
                      step="100"
                      min="100"
                      value={festivalForm.default_amount}
                      onChange={(e) => setFestivalForm({ ...festivalForm, default_amount: e.target.value })}
                      placeholder="12500"
                      required
                      className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent transition"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">
                      Tenure (Months) *
                    </label>
                    <input
                      type="number"
                      min="1"
                      max="36"
                      value={festivalForm.default_installments}
                      onChange={(e) => setFestivalForm({ ...festivalForm, default_installments: e.target.value })}
                      placeholder="10"
                      required
                      className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent transition"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">
                    Description (Optional)
                  </label>
                  <textarea
                    value={festivalForm.description}
                    onChange={(e) => setFestivalForm({ ...festivalForm, description: e.target.value })}
                    placeholder="Provide details about the festival scheduler..."
                    rows="3"
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent transition text-sm"
                  />
                </div>
              </div>
              <div className="p-4 bg-gray-50 border-t border-gray-100 flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setShowCreateFestivalModal(false)}
                  className="px-4 py-2 border border-gray-200 text-gray-600 font-semibold rounded-xl hover:bg-gray-100 transition text-sm"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-5 py-2 bg-purple-700 hover:bg-purple-800 text-white font-bold rounded-xl shadow-md transition text-sm"
                >
                  Create Festival
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Festival Modal */}
      {showEditFestivalModal && editingFestival && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 backdrop-blur-xs">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 overflow-hidden border border-gray-100 transform transition-all duration-300">
            <div className="p-5 bg-gradient-to-r from-purple-900 to-indigo-900 text-white flex items-center justify-between">
              <h2 className="text-lg font-bold">Edit Festival</h2>
              <button
                onClick={() => setShowEditFestivalModal(false)}
                className="p-1 hover:bg-purple-800 text-purple-100 rounded-lg transition"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleUpdateFestival}>
              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-500 mb-1">
                    Festival Code
                  </label>
                  <input
                    type="text"
                    value={editingFestival.festival_code}
                    disabled
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl bg-gray-50 text-gray-500 font-semibold"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">
                    Festival Name *
                  </label>
                  <input
                    type="text"
                    value={editingFestival.festival_name}
                    onChange={(e) => setEditingFestival({ ...editingFestival, festival_name: e.target.value })}
                    required
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent transition"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">
                      Standard Amount (₹) *
                    </label>
                    <input
                      type="number"
                      step="100"
                      min="100"
                      value={editingFestival.default_amount || '12500.00'}
                      onChange={(e) => setEditingFestival({ ...editingFestival, default_amount: e.target.value })}
                      required
                      className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent transition"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">
                      Tenure (Months) *
                    </label>
                    <input
                      type="number"
                      min="1"
                      max="36"
                      value={editingFestival.default_installments || 10}
                      onChange={(e) => setEditingFestival({ ...editingFestival, default_installments: e.target.value })}
                      required
                      className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent transition"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">
                    Description (Optional)
                  </label>
                  <textarea
                    value={editingFestival.description || ''}
                    onChange={(e) => setEditingFestival({ ...editingFestival, description: e.target.value })}
                    rows="3"
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent transition text-sm"
                  />
                </div>
                <div className="flex items-center gap-3 pt-2">
                  <input
                    type="checkbox"
                    id="is_active_checkbox"
                    checked={editingFestival.is_active}
                    onChange={(e) => setEditingFestival({ ...editingFestival, is_active: e.target.checked })}
                    className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                  />
                  <label htmlFor="is_active_checkbox" className="text-sm font-semibold text-gray-700 cursor-pointer select-none">
                    Active (Allows employee self-service application selection)
                  </label>
                </div>
              </div>
              <div className="p-4 bg-gray-50 border-t border-gray-100 flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setShowEditFestivalModal(false)}
                  className="px-4 py-2 border border-gray-200 text-gray-600 font-semibold rounded-xl hover:bg-gray-100 transition text-sm"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-5 py-2 bg-purple-700 hover:bg-purple-800 text-white font-bold rounded-xl shadow-md transition text-sm"
                >
                  Save
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default FestivalAdvanceManagement;
